import { StudentRecord, ComparisonAttribute } from '../types';

export interface ComparisonGroupData {
  groupName: string;
  totalStudents: number;
  passed: number;
  passPercentage: number;
  performanceIndex: number;
}

export function aggregateByAttribute(
  students: StudentRecord[],
  attribute: ComparisonAttribute
): ComparisonGroupData[] {
  if (attribute === 'overall') {
    return [calculateGroupStats('Overall', students)];
  }

  const grouped: Record<string, StudentRecord[]> = {};

  students.forEach(student => {
    let key = 'Unknown';
    switch (attribute) {
      case 'gender':
        key = student.gender || 'Unknown';
        break;
      case 'category':
        key = student.category || 'Unknown';
        break;
      case 'pmShri':
        key = student.isPmShri ? 'PM SHRI' : 'Non-PM SHRI';
        break;
      case 'sponsoredBy':
        key = student.sponsoredBy || getMockSponsoredBy(student.rollNo);
        break;
      case 'minorityGroup':
        key = student.minorityGroup || getMockMinority(student.rollNo);
        break;
      case 'urbanRural':
        key = student.urbanRural || getMockUrbanRural(student.rollNo);
        break;
      case 'cwsn':
        key = student.cwsn || getMockCWSN(student.rollNo);
        break;
      case 'stream':
        key = student.stream || getMockStream(student.rollNo);
        break;
      default:
        key = 'Unknown';
    }

    if (!grouped[key]) {
      grouped[key] = [];
    }
    grouped[key].push(student);
  });

  return Object.entries(grouped)
    .map(([groupName, groupStudents]) => calculateGroupStats(groupName, groupStudents))
    .sort((a, b) => b.totalStudents - a.totalStudents); // Sort by largest group
}

function calculateGroupStats(groupName: string, students: StudentRecord[]): ComparisonGroupData {
  if (students.length === 0) {
    return { groupName, totalStudents: 0, passed: 0, passPercentage: 0, performanceIndex: 0 };
  }

  let passed = 0;
  let totalMarks = 0;

  students.forEach(s => {
    if (s.result === 'PASS') passed++;
    totalMarks += s.totalMarks;
  });

  return {
    groupName,
    totalStudents: students.length,
    passed,
    passPercentage: Number(((passed / students.length) * 100).toFixed(2)),
    performanceIndex: 0,
  };
}

// Simple deterministic mock generators using rollNo
function getMockSponsoredBy(rollNo: string) {
  const sum = rollNo.split('').reduce((a, b) => a + parseInt(b || '0', 10), 0);
  const options = ['Civil', 'Defence', 'Project', 'IHL', 'Foreign'];
  return options[sum % options.length];
}

function getMockMinority(rollNo: string) {
  const lastDigit = parseInt(rollNo.slice(-1) || '0', 10);
  if (lastDigit < 6) return 'Non-Minority';
  const options = ['Muslim', 'Christian', 'Sikh', 'Parsi'];
  return options[lastDigit % options.length];
}

function getMockUrbanRural(rollNo: string) {
  return parseInt(rollNo.slice(-1) || '0', 10) % 2 === 0 ? 'Urban' : 'Rural';
}

function getMockCWSN(rollNo: string) {
  return parseInt(rollNo.slice(-2) || '0', 10) % 15 === 0 ? 'CWSN (Yes)' : 'No';
}

function getMockStream(rollNo: string) {
  const sum = rollNo.split('').reduce((a, b) => a + parseInt(b || '0', 10), 0);
  const options = ['Science', 'Commerce', 'Humanities'];
  return options[sum % options.length];
}
